import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { adminCustomisedWorkspaces, adminPlatformBranding, adminRemovePlatformLogo, adminRemoveWorkspaceBranding, adminUpdatePlatformBranding, adminUploadPlatformLogo, type CustomisedWorkspace, type PlatformBrandingView } from '../../../api/brandingApi';
import { BrandingForm } from '../../branding/BrandingForm';
import { S, fmtWhen } from './adminPageStyles';

/** Admin › Branding (SRS #102): the platform's own brand (what every customer without white-labelling sees)
 *  and the workspaces that carry their own — with the master admin able to remove one. */
const AdminBrandingPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [view, setView] = useState<PlatformBrandingView | null>(null);
  const [workspaces, setWorkspaces] = useState<CustomisedWorkspace[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([adminPlatformBranding(), adminCustomisedWorkspaces()])
      .then(([v, w]) => { setView(v); setWorkspaces(w.workspaces); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (w: CustomisedWorkspace) => {
    if (!window.confirm(`Remove the custom logo, colours and watermark of ${w.email}? They will see the platform brand.`)) return;
    try { await adminRemoveWorkspaceBranding(w.owner_user_id); setNotice(`Branding removed for ${w.email}`); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Remove failed'); }
  };

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Branding" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Platform brand</h2>
        <p style={S.sub}>What every customer sees unless their workspace has its own branding (Enterprise, Government and Firm plans). Empty fields use the built-in defaults.</p>
        {error && <div style={S.error}>{error}</div>}
        {notice && <div style={S.ok}>{notice}</div>}
        {view && (
          <BrandingForm stored={view.branding} inherited={view.defaults} effective={view.effective} limits={view.limits}
            onSave={async (patch) => { await adminUpdatePlatformBranding(patch); load(); }}
            onUpload={async (f) => { await adminUploadPlatformLogo(f); load(); }}
            onRemoveLogo={async () => { await adminRemovePlatformLogo(); load(); }} />
        )}
        <h2 style={{ ...S.h2, marginTop: 24 }}>Workspaces with their own branding</h2>
        <p style={S.sub}>Customers on a white-label plan who have set a logo, colours or watermark. "Plan lapsed" means the branding is stored but no longer rendered.</p>
        <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 8 }}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Customer</th><th style={S.th}>Brand name</th><th style={S.th}>Logo</th><th style={S.th}>Status</th><th style={S.th}>Updated</th><th style={S.th}></th></tr></thead>
            <tbody>
              {workspaces.length === 0 && <tr><td style={S.td} colSpan={6}><span style={S.muted}>No workspace has customised its branding yet.</span></td></tr>}
              {workspaces.map((w) => (
                <tr key={w.owner_user_id} data-testid={`ws-${w.owner_user_id}`}>
                  <td style={S.td}>{w.email}{w.organisation && <div style={{ ...S.muted, fontSize: '0.78rem' }}>{w.organisation}</div>}</td>
                  <td style={S.td}>{w.brand_name || <span style={S.muted}>—</span>}</td>
                  <td style={S.td}>{w.has_logo ? 'yes' : '—'}</td>
                  <td style={S.td}><span style={{ ...S.chip, ...(w.allowed ? {} : { color: 'var(--ds-warning-text)' }) }}>{w.allowed ? 'active' : 'plan lapsed'}</span></td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtWhen(w.updated_at)}<div style={{ ...S.muted, fontSize: '0.72rem' }}>{w.updated_by}</div></td>
                  <td style={S.td}><button style={S.danger} onClick={() => remove(w)}>Remove branding</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </DashboardLayout>
  );
};

export default AdminBrandingPage;
